import React, { useState, useEffect, useCallback } from 'react'
import { MessageSquare, Send, User } from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Toast } from '../ui/toast'
import { smsApi, SMS_SENT_LEAD_ARCHIVED } from '../../services/smsApi'

interface SMSConversationProps {
  leadId: string
  leadName: string
  leadPhone: string
  onArchived?: () => void | Promise<void>
}

interface SMSMessage {
  id: string
  lead_phone: string
  lead_name: string
  direction: 'inbound' | 'outbound'
  body: string
  status: string
  sent_by_name?: string
  created_at: string
}

export default function SMSConversationCard({ leadId, leadName, leadPhone, onArchived }: Readonly<SMSConversationProps>) {
  const [messages, setMessages] = useState<SMSMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [replyText, setReplyText] = useState('')
  
  // Toast states
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  // Fetch SMS conversation
  const fetchConversation = useCallback(async () => {
    try {
      setLoading(true)
      const data = await smsApi.getConversation(leadId)
      setMessages(data.messages || [])
      
      // Mark messages as read when viewing conversation
      try {
        await smsApi.markMessagesRead(leadId)
        console.log('✓ Messages marked as read for lead:', leadId)
      } catch (markReadError) {
        // Don't fail the whole load if marking as read fails
        console.warn('Failed to mark messages as read:', markReadError)
      }
    } catch (error) {
      console.error('Error fetching SMS conversation:', error)
      setToastMessage('Failed to load SMS conversation')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }, [leadId])

  // Send SMS reply
  const sendReply = async () => {
    if (!replyText.trim()) {
      setToastMessage('Please enter a message')
      setToastType('error')
      setShowToast(true)
      return
    }

    try {
      setSending(true)
      const result = await smsApi.sendSms({
        lead_id: leadId,
        message_body: replyText.trim()
      })
      
      if (result.error) {
        throw new Error(result.message || 'Failed to send SMS')
      }

      // Clear reply text and refresh conversation
      setReplyText('')
      if (result.archived) {
        setToastMessage(SMS_SENT_LEAD_ARCHIVED)
        await onArchived?.()
      } else {
        setToastMessage('SMS sent successfully!')
      }
      setToastType('success')
      setShowToast(true)
      
      // Refresh conversation to show new message
      await fetchConversation()
      
    } catch (error: any) {
      console.error('Error sending SMS:', error)
      setToastMessage(error.message || 'Failed to send SMS')
      setToastType('error')
      setShowToast(true)
    } finally {
      setSending(false)
    }
  }

  // Load conversation on mount
  useEffect(() => {
    fetchConversation()
  }, [fetchConversation])

  // Auto-refresh conversation every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchConversation, 30000)
    return () => clearInterval(interval)
  }, [fetchConversation])

  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
      case 'received':
        return 'text-green-600'
      case 'failed':
        return 'text-red-600'
      case 'queued':
        return 'text-yellow-600'
      default:
        return 'text-gray-600'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
      case 'received':
        return '✓'
      case 'failed':
        return '✗'
      case 'queued':
        return '⏳'
      default:
        return '?'
    }
  }

  return (
    <>
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-200">
          <CardTitle className="flex items-center gap-2 text-lg text-slate-900">
            <MessageSquare className="h-5 w-5" />
            SMS Conversation
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
              <span className="ml-2 text-slate-600">Loading conversation...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Messages */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {messages.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    <p>No SMS messages yet</p>
                    <p className="text-sm">Start a conversation by sending a message below</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.direction === 'outbound'
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-100 text-slate-900'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex items-center gap-1">
                            {message.direction === 'outbound' ? (
                              <User className="h-3 w-3" />
                            ) : (
                              <MessageSquare className="h-3 w-3" />
                            )}
                            <span className="text-xs font-medium">
                              {message.direction === 'outbound' 
                                ? (message.sent_by_name || 'You')
                                : leadName
                              }
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className={`text-xs ${getStatusColor(message.status)}`}>
                              {getStatusIcon(message.status)}
                            </span>
                            <span className="text-xs opacity-75">
                              {formatTimestamp(message.created_at)}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{message.body}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Reply Section */}
              <div className="border-t border-slate-200 pt-4">
                <div className="space-y-3">
                  <div>
                    <textarea
                      placeholder={`Reply to ${leadName}...`}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      className="w-full min-h-[80px] p-3 border border-slate-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      maxLength={1600}
                    />
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-slate-500">
                        {replyText.length}/1600 characters
                      </span>
                      <span className="text-xs text-slate-500">
                        To: {leadPhone}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button
                      onClick={sendReply}
                      disabled={sending || !replyText.trim()}
                      className="bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      {sending ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send SMS
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </>
  )
}
